import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Button,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Select,
  Theme,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { CartItemAdditionTypes } from '../../constants/event';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import {
  cartActions,
  setSequenceId,
  setCartItemCollapseState,
} from '../../reducers/cartSlice';
import {
  dialogActions,
  selectItemAndResetNavigation,
  resetDialogSelectedItem,
} from '../../reducers/dialogSlice';
import { AgentInterceptionType } from '../../reducers/messagingSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import {
  CartItem,
  getAutoCombinedCartItemIdsCount,
  shouldCollapse,
} from '../../utils/cart';
import Colors from '../../utils/color';
import CartItemModGroups from './CartItemModGroups';
import OrderProgressBar from './OrderProgressBar';
import { upsell } from '../../utils/upsell';

import {
  addRefreshNonComboItem,
  cloneDeleteMUComboItem,
  refreshMUAutoCombinedItems,
  resetCombinedCartItemds,
} from '../../redux/features/autoCombo/autoCombo.slice';
import useAutoCombo from '../../hooks/autoCombo/useAutoCombo.hooks';
import { useAppSelector } from '../../app/hooks';
import { selectCartItemsQuantity } from '../../selectors/cart';
import { selectMenuModuleCartErrors } from '../../selectors/order';
import {
  cartErrorMatchesItem,
  getModifierErrorPaths,
} from '../../utils/menuModuleError';
import { ItemCartActionButtons } from '../../constants/enums';
import useMenuUnificationOrderHandlers from '../../hooks/menuUnification/useMenuUnificationOrderHandler.hooks';
interface ICartItemReview {
  cartItem: CartItem;
  quantity: number;
  status?: OrderStatus;
  collapseState: number;
}

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    marginTop: theme.spacing(2),
  },
  content: {
    padding: `${theme.spacing(1.5)} !important`,
    '& > div.cartitem-mod-group': {
      padding: '0 !important',
    },
  },
  mainItemname: {
    lineHeight: '1.57 !important',
    fontSize: `${theme.spacing(1.5)} !important`,
    fontWeight: 'bold !important',
  },
  lineItemsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    minHeight: '24px',
  },
  itemPrice: {
    paddingLeft: theme.spacing(2),
    fontSize: '0.75rem !important',
  },
  actionsWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    '& span': {
      cursor: 'pointer',
    },
  },
  itemActions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    '& .MuiButtonBase-root.MuiButton-root': {
      textTransform: 'none',
      color: Colors.black,
      fontWeight: 'bold',
      flexDirection: 'row-reverse',
      textAlign: 'right',
      padding: '0px',
    },
  },
  selectQuantity: {
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
    '&:before': {
      borderBottom: 'none !important',
    },
    '& .MuiSelect-select': {
      textAlign: 'center',
      minWidth: '40px !important',
      paddingRight: '2px !important',
      paddingLeft: '0px !important',
    },
  },
  autoCombinedText: {
    paddingBottom: theme.spacing(1),
  },
}));

export const CartItemReview = (props: ICartItemReview) => {
  const { cartItem, quantity, status, collapseState } = props;
  const cartItemRef = useRef<HTMLDivElement>(null);
  const actionableElementsRef = useRef<any[]>([]);
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    isUnifiedMenu,
    isPRPAutoComboEnabled,
    isBMAutoComboEnabled,
    autoCombinedCartItemsQuantity,
  } = useAutoCombo();
  const { sequenceId, cartItems } = useSelector(
    (state: RootState) => state.cart
  );
  const { selectedItemCartId } = useSelector(
    (state: RootState) => state.dialog
  );

  const { sendAgentInterception } = useAgentInterception();
  const quantityList: number[] = new Array(19).fill(0);

  const isPRPAutoCombined = !!(
    isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds?.length
  );
  const isMUAutoCombined = !!(
    isUnifiedMenu &&
    isBMAutoComboEnabled &&
    cartItem.isAutoCombined
  );
  const isCartItemAutoCombined = isPRPAutoCombined || isMUAutoCombined;
  const cartItemsQuantity = useAppSelector(selectCartItemsQuantity);
  const menuModuleCartErrors = useAppSelector(selectMenuModuleCartErrors);

  const handleCollapseStateChange = useCallback(() => {
    dispatch(setCartItemCollapseState({ cartItemId: cartItem.cartItemId }));
  }, [dispatch]);

  const sendAgentInterventionEvent = (description: string) => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.ORDER_CHANGED,
        description,
      },
    });
  };

  const {
    menuUnificationOrderFailureCallback,
    menuUnificationOrderSuccessCallback,
  } = useMenuUnificationOrderHandlers();

  const cloneDeleteAutoCombinedCartItems = (operator: string) => {
    if (isMUAutoCombined) {
      dispatch(
        cloneDeleteMUComboItem({
          cartItemId: cartItem.cartItemId.toString(),
          operator,
          sequenceId,
        })
      );
      if (operator === '+') {
        dispatch(setSequenceId(sequenceId + 1));
      } else if (
        operator === '-' &&
        Object.keys(autoCombinedCartItemsQuantity).length === 1
      ) {
        // Only 1 item and it is being deleted -- so clear the entire cart
        dispatch(resetDialogSelectedItem());
        dispatch(cartActions.clearCart());
        dispatch(resetCombinedCartItemds());
      }
      dispatch(
        refreshMUAutoCombinedItems({
          menuUnificationOrderFailureCallback,
          menuUnificationOrderSuccessCallback,
        })
      );
    }
  };

  const cloneCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('+');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] +
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      const cartItemIdString = cartItem.cartItemId.toString();

      // The quantity shown to user is used for cloning the item --- as for brand-menu non-combined item the quantity may not match
      dispatch(
        cartActions.cloneCartItem({
          id: cartItemIdString,
          quantity,
        })
      );
      dispatch(dialogActions.cloneSelectedItem({ cartItemId: sequenceId + 1 }));
    }

    sendAgentInterventionEvent(`Clone ${cartItem.name} in the same order`);
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };

  const deleteCartItem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (isMUAutoCombined) {
      cloneDeleteAutoCombinedCartItems('-');
    } else if (isPRPAutoComboEnabled && cartItem.autoCombinedCartItemIds) {
      const autoCombinedCartItemIdsCount = getAutoCombinedCartItemIdsCount(
        cartItem.autoCombinedCartItemIds
      );
      Object.keys(autoCombinedCartItemIdsCount).forEach((cartItemId) =>
        dispatch(
          cartActions.updateQuantity({
            cartItemId,
            quantity:
              cartItemsQuantity[cartItemId] -
              autoCombinedCartItemIdsCount[cartItemId],
          })
        )
      );
    } else {
      dispatch(cartActions.deleteCartItem(cartItem.cartItemId.toString()));
      if (selectedItemCartId === cartItem.cartItemId) {
        const cartItemIds = Object.keys(cartItems)
          .filter((id) => id !== selectedItemCartId.toString())
          .map((id) => parseInt(id));
        const newCartItemId = Math.max(...cartItemIds);
        if (newCartItemId > 0) {
          dispatch(
            dialogActions.selectItemAndResetNavigation({
              item: cartItems[newCartItemId.toString()],
              itemCartId: newCartItemId,
            })
          );
        } else {
          dispatch(dialogActions.selectItemAndResetNavigation(undefined));
        }
      }
    }
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
    sendAgentInterventionEvent(`${cartItem.name} is removed from order`);
  };

  const updateQuantity = (newQuantity: number) => {
    if (isCartItemAutoCombined) return; // not support for auto combined item

    const cartItemIdString = cartItem.cartItemId.toString();
    if (Object.keys(autoCombinedCartItemsQuantity).includes(cartItemIdString)) {
      // Update the quantity in non-combined autoComboItemQuantity
      dispatch(
        addRefreshNonComboItem({
          cartItemId: cartItemIdString,
          cartItem,
          quantity: newQuantity,
        })
      );
    }

    dispatch(
      cartActions.updateQuantity({
        cartItemId: cartItemIdString,
        quantity: newQuantity,
      })
    );
    sendAgentInterventionEvent(
      `Quantity of ${cartItem.name} changed to ${newQuantity}`
    );
  };

  const handleDecreaseQuantity = () => {
    if (quantity > 1) {
      updateQuantity(quantity - 1);
    }
  };

  const handleIncreaseQuantity = () => {
    if (quantity < 19) {
      updateQuantity(quantity + 1);
    }
  };

  const onCardClick = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;

    const isSelectDropdownClick =
      target.closest('.MuiMenuItem-root') ||
      target.closest('.MuiMenu-root') ||
      target.closest('.MuiPaper-root[role="presentation"]');

    const isActionableClick =
      actionableElementsRef.current.some(
        (element) => element && element.contains(target)
      ) || isSelectDropdownClick;

    if (!isActionableClick) {
      handleCollapseStateChange();
    }

    if (isCartItemAutoCombined) return;
    dispatch(
      dialogActions.selectItemAndResetNavigation({
        item: cartItem,
        itemCartId: cartItem.cartItemId,
      })
    );
    if (isUnifiedMenu) {
      // Check upsell and auto reject it if the cart context changes
      upsell.settle();
    }
  };
  const collapse = shouldCollapse(collapseState, status);
  // A menu-module error whose path is just this item (no modifier segments)
  // means the item itself is the failing node — highlight its name.
  const isItemLevelError = menuModuleCartErrors.some(
    (error) =>
      cartErrorMatchesItem(error, cartItem.cartItemId, cartItem.name) &&
      error.nodePath.length === 1
  );
  const itemColor = isItemLevelError ? Colors.Thunderbird : Colors.daintree;
  const itemCardBorder =
    selectedItemCartId === cartItem.cartItemId
      ? `3px solid ${itemColor}`
      : 'unset';

  useEffect(() => {
    if (selectedItemCartId === cartItem.cartItemId) {
      cartItemRef.current?.scrollIntoView();
    }
  }, [selectedItemCartId]);

  return (
    <Card
      id="cart-item"
      className={classes.card}
      elevation={3}
      sx={{
        borderRadius: 1.5,
        cursor: isCartItemAutoCombined ? 'default' : 'pointer',
        backgroundColor: `${
          cartItem.addedBy === CartItemAdditionTypes.AI
            ? Colors.aiItemIndicator
            : isCartItemAutoCombined
            ? Colors.gallery
            : Colors.white
        }`,
      }}
      onClick={onCardClick}
      ref={cartItemRef}
      style={{
        borderLeft: itemCardBorder,
        borderRight: itemCardBorder,
        borderTop: itemCardBorder,
      }}
    >
      <CardContent className={classes.content}>
        <div className={classes.lineItemsWrapper}>
          <Typography
            variant="subtitle2"
            className={classes.mainItemname}
            style={{ color: itemColor }}
          >
            {cartItem.name}
          </Typography>
          {collapse ? (
            <ExpandMoreIcon style={{ padding: 0 }}></ExpandMoreIcon>
          ) : (
            <ExpandLessIcon style={{ padding: 0 }}></ExpandLessIcon>
          )}
        </div>
        {collapse ? null : (
          <>
            {isCartItemAutoCombined && (
              <Typography
                variant="subtitle2"
                className={`${classes.mainItemname} ${classes.autoCombinedText}`}
                style={{ color: itemColor }}
              >
                (Auto-combined)
              </Typography>
            )}
            {cartItem && cartItem.childModifierGroups && (
              <CartItemModGroups
                cartItem={cartItem}
                modGroupCartItem={cartItem}
                quantity={quantity}
                selectable={!isCartItemAutoCombined}
                errorNodePaths={getModifierErrorPaths(
                  menuModuleCartErrors,
                  cartItem.cartItemId,
                  cartItem.name
                )}
              />
            )}
          </>
        )}

        <div className={classes.actionsWrapper}>
          <div>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.DECREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleDecreaseQuantity}
              disabled={isCartItemAutoCombined}
            >
              <RemoveIcon />
            </IconButton>
            <Select
              value={quantity}
              size="small"
              variant="standard"
              onChange={(e) => updateQuantity(e.target.value as number)}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 200 },
                },
              }}
              className={classes.selectQuantity}
              disabled={isCartItemAutoCombined}
            >
              {quantityList.map((q, index) => (
                <MenuItem
                  key={`quantity-${index}`}
                  value={index + 1}
                  id={`quantity-${index}`}
                >
                  {index + 1}
                </MenuItem>
              ))}
            </Select>
            <IconButton
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.INCREASE_QUANTITY
                ] = el)
              }
              size="small"
              onClick={handleIncreaseQuantity}
              disabled={isCartItemAutoCombined}
            >
              <AddIcon />
            </IconButton>
          </div>
          <div className={classes.itemActions}>
            <Button
              ref={(el) =>
                (actionableElementsRef.current[ItemCartActionButtons.REMOVE] =
                  el)
              }
              variant="text"
              onClick={deleteCartItem}
              size="small"
            >
              Remove
            </Button>
            <Button
              ref={(el) =>
                (actionableElementsRef.current[
                  ItemCartActionButtons.DUPLICATE
                ] = el)
              }
              variant="text"
              onClick={cloneCartItem}
              size="small"
            >
              Duplicate
            </Button>
          </div>
        </div>
      </CardContent>
      <OrderProgressBar status={status} cartItem={cartItem} />
    </Card>
  );
};
